/* eslint-disable  */
import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import ClassroomsScreen from "../../components/screens/Classrooms/ClassroomsScreen";
import { classrooms as classroomQuerys } from "@nualang/nualang-api-and-queries/Queries";
import ClassroomImage from "../../img/classroom.svg";
import MembersImage from "../../img/members.svg";
import {
  getClassroomMembers,
  getClassroomMember,
  createClassroomMember,
  deleteClassroomMember,
} from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import { getClassrooms } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { classrooms as classroomHelpers, users as usersHelpers } from "../../utils/helpers";
import config, { featureFlags } from "../../config";
import { useApp, useClassroom, useSubscription } from "../../hooks";

function Classrooms(props) {
  const {
    authData: { username, attributes = {} },
    navigate,
    isCommunityAccessRestricted,
  } = props;
  const { setNewClassroom, setIsJoinClassroomDialogOpen, memberLimit } =
    useClassroom();


  const { subscription } = useSubscription();
  const { openSnackbar, startLoading, stopLoading, markCommunityAccessRestricted } =
    useApp();

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Mutations
  const joinClassroomMutation = classroomQuerys.useJoinClassroom(
    createClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("joined_classroom", "success");
        navigate(`/classrooms/${variables.classroomId}`);
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            memberId: variables.username,
            verbose: false,
          }),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const leaveClassroomMutation = classroomQuerys.useLeaveClassroom(
    deleteClassroomMember,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (data, variables) => {
        openSnackbar("left_classroom", "success");
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMembers(
            variables.classroomId,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.itemMember(
            variables.classroomId,
            variables.username,
            variables.username,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: classroomQuerys.classroomKeys.list({
            memberId: username,
            verbose: false,
          }),
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const learnClassroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        memberId: username,
        verbose: false,
        limit: config.limits.learnClassrooms,
      },
    },
    { enabled: !!username },
  );

  const teachClassroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        createdBy: username,
        verbose: false,
        // Get all using high limit
        limit: config.limits.teachClassrooms,
      },
    },
    { enabled: !!username && attributes["custom:role"] === "teach" },
  );

  // Fallback signal: a classroom already flagged by the backend as
  // community-access-restricted may arrive here before the user's own record
  // ever gets flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([
      ...(learnClassroomsQuery.data || []),
      ...(teachClassroomsQuery.data || []),
    ]);
  }, [
    learnClassroomsQuery.data,
    teachClassroomsQuery.data,
    markCommunityAccessRestricted,
  ]);

  const learnClassrooms = useMemo(
    () =>
      learnClassroomsQuery.isSuccess &&
      Array.isArray(learnClassroomsQuery.data) &&
      learnClassroomsQuery.data.length
        ? learnClassroomsQuery.data.filter((c) => c.visibility !== "archived")
        : [],
    [learnClassroomsQuery.isSuccess, learnClassroomsQuery.data],
  );
  const teachClassrooms = useMemo(
    () =>
      teachClassroomsQuery.isSuccess &&
      Array.isArray(teachClassroomsQuery.data) &&
      teachClassroomsQuery.data.length
        ? teachClassroomsQuery.data.filter((c) => c.visibility !== "archived")
        : [],
    [teachClassroomsQuery.isSuccess, teachClassroomsQuery.data],
  );
  const archivedClassrooms = useMemo(
    () =>
      teachClassroomsQuery.isSuccess &&
      Array.isArray(teachClassroomsQuery.data) &&
      teachClassroomsQuery.data.length
        ? teachClassroomsQuery.data.filter((c) => c.visibility === "archived")
        : [],
    [teachClassroomsQuery.isSuccess, teachClassroomsQuery.data],
  );

  const handleJoinClassroom = (classroom, memberCount) => {
    setNewClassroom(classroom);
    if (memberCount >= memberLimit) {
      openSnackbar("classroom_member_limit_reached", "error");
    } else if (classroom.visibility === "private") {
      setIsJoinClassroomDialogOpen(true);
    } else {
      startLoading();
      joinClassroomMutation.mutate({
        classroomId: classroom.classroomId,
        username,
      });
    }
  };

  const handleLeaveClassroom = async (classroomId) => {
    startLoading();
    leaveClassroomMutation.mutate({ classroomId, username });
  };

  const handleViewClassroom = (classroomId) => {
    navigate(`/classrooms/${classroomId}`);
  };

  const handleSearchClassrooms = isCommunityAccessRestricted
    ? undefined
    : () => navigate("/classrooms/search");

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const getMemberDetails = (members) => usersHelpers.fetchMembersDetails(members);
  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  return (
    <ClassroomsScreen
      t={t}
      role={attributes["custom:role"]}
      isLoading={
        learnClassroomsQuery.isLoading || teachClassroomsQuery.isLoading
      }
      placeholderImageUrl={ClassroomImage}
      memberPlaceholderImageUrl={MembersImage}
      learnClassrooms={learnClassrooms}
      teachClassrooms={teachClassrooms}
      archivedClassrooms={archivedClassrooms}
      handleJoinClassroom={handleJoinClassroom}
      handleLeaveClassroom={handleLeaveClassroom}
      handleViewClassroom={handleViewClassroom}
      handleSearchClassrooms={handleSearchClassrooms}
      getMemberDetails={getMemberDetails}
      getClassroomMembers={getClassroomMembers}
      getClassroomMember={getClassroomMember}
      username={username}
      membersProps={{
        handleViewActivity,
      }}
      subscription={subscription}
      handleViewUserProfile={handleViewUserProfile}
      mainSiteUrl={config.SITE_URL}
      featureFlags={featureFlags}
    />
  );
}

Classrooms.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  isCommunityAccessRestricted: PropTypes.bool,
};

export default Classrooms;
